"use client"

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";

import { motion } from "framer-motion";

const TopDrawer = dynamic(() => import("@/components/drawer"), { ssr: false });
const logoBanner = "/banner_logo.svg"
const logomark = "/logomark.svg"

export default function Nav({ inView }: { inView: boolean | null }) {
    const router = useRouter();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    setScrolled(window.scrollY > 100);
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <>
            {/* Desktop Navbar */}
            <div className="hidden md:flex justify-center w-full fixed top-0 left-0 right-0 z-50 pointer-events-none pt-6">
                <motion.div
                    className={`flex justify-center pointer-events-auto ${scrolled && "outline-1 outline-primary-900 rounded-full bg-primary-1000/25"}`}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ backdropFilter: scrolled ? "blur(24px)" : "blur(0px)", opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                    <div className="flex justify-center items-center text-primary-100 font-semibold">
                        {/* LEFT */}
                        <div className="flex gap-10 pl-10">
                            <NavbarItem text="TALENT" href="#talent" />
                            <NavbarItem text="AUDITION" href="#audition" />
                        </div>

                        {/* LOGO */}
                        <motion.div
                            className="flex min-w-24 justify-center -my-2"
                            initial={{ marginLeft: 100, marginRight: 100 }}
                            animate={{
                                marginLeft: scrolled ? 32 : 100,
                                marginRight: scrolled ? 32 : 100,
                                scale: scrolled ? 0.75 : 1,
                            }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            whileHover={{ scale: scrolled ? 0.85 : 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <Image
                                className="cursor-pointer"
                                src={logomark}
                                alt="Logo"
                                width={120}
                                height={120}
                                priority
                                onClick={() => {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                    router.replace("");
                                }}
                            />
                        </motion.div>

                        {/* RIGHT */}
                        <div className="flex gap-10 pr-10">
                            <NavbarItem text="OUR TEAM" href="#team" />
                            <NavbarItem text="CONTACT" href="#contact" />
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Mobile Navbar */}
            {inView !== null && (
                <>
                    <motion.div
                        className={`md:hidden fixed top-0 left-0 right-0 z-40 h-32 pb-10 flex items-center justify-center bg-linear-to-b from-primary-1000 via-primary-1000 via-70% to-transparent ${inView ? "hidden" : ""}`}
                        animate={{ opacity: inView ? 0 : 1, y: inView ? -20 : 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                        <Image
                            className="select-none"
                            src={logoBanner}
                            alt="Logo"
                            width={150}
                            height={75}
                            priority
                            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        />
                    </motion.div>

                    <div className="md:hidden fixed left-6 top-9 z-60">
                        <div
                            id="nav-icon3"
                            className={open ? "open" : ""}
                            onClick={() => setOpen((v) => !v)}
                            role="button"
                            aria-label="Toggle menu"
                            aria-expanded={open}
                        >
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>

                    <div className="md:hidden">
                        <TopDrawer open={open} setOpen={setOpen}>
                            <div className="flex justify-end p-4">
                                <Image className="select-none" src={logomark} alt="Logomark" width={75} height={75} priority />
                            </div>
                            <div className="flex flex-col items-center justify-center text-primary-100 gap-10 font-semibold text-3xl">
                                <Link href="#talent" className="inline-block px-3 rounded-full">TALENTS</Link>
                                <Link href="#audition" className="inline-block px-3 rounded-full">AUDITION</Link>
                                <Link href="#team" className="inline-block px-3 rounded-full">OUR TEAM</Link>
                                <Link href="#contact" className="inline-block px-3 rounded-full">CONTACT</Link>
                            </div>
                        </TopDrawer>
                    </div>
                </>
            )}
        </>
    );
}

function NavbarItem({ text, href }: { text: string; href: string }) {
    return (
        <div className="flex-1 min-w-16 text-center">
            <motion.span
                className="relative inline-block cursor-pointer"
                initial="rest"
                animate="rest"
                whileHover="hover"
                whileTap="tap"
                variants={{ rest: { scale: 1 }, tap: { scale: 0.92 } }}
                transition={{ duration: 0.2, ease: "easeOut" }}
            >
                <motion.span
                    variants={{
                        rest: { scale: 0.85, opacity: 0 },
                        hover: { scale: 1, opacity: 1 },
                        tap: { scale: 0.95 },
                    }}
                    transition={{ type: "spring", stiffness: 650, damping: 20 }}
                    className="pointer-events-none absolute -inset-3 rounded-3xl border-2 border-primary-100"
                />
                <Link href={href} className="relative z-10 inline-block text-xl lg:text-2xl text-nowrap">
                    {text}
                </Link>
            </motion.span>
        </div>
    );
}